import React from 'react';
import { Award, TrendingUp, Target } from 'lucide-react';
import { TRAININGS } from '../trainings';
import { projectLevelUp, suggestSpecialties } from '../../../utils/careerProjection';

/**
 * CareerPanel — driver-level career projection + specialty suggestions.
 *
 * Extracted from `parts.jsx` on 31/05/2026 to keep parts.jsx light.
 * Visual output and data-testids preserved verbatim.
 */
export function CareerPanel({ driver, isOnMission, activeContracts }) {
  const projection = projectLevelUp({ ...driver, _currentlyOnMission: isOnMission });
  const specTrainings = TRAININGS.filter(tr => tr.group === 'spec');
  const suggestions = suggestSpecialties(driver, activeContracts || [], specTrainings);
  return (
    <div className="mt-3 border border-money/40 rounded-lg p-3 bg-money/5 space-y-3" data-testid={`career-panel-${driver.id}`}>
      <p className="text-xs font-semibold text-money flex items-center gap-1">
        <Target className="w-3.5 h-3.5" /> Plan de carrière
      </p>
      {projection && (
        <div className="rounded-md bg-ink-700/30 border border-ink-600 p-2.5" data-testid={`career-projection-${driver.id}`}>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-money" /> Progression niveau</span>
            <span className="font-mono text-slate-300">
              Niv. {projection.currentLevel} → <strong className="text-money">Niv. {projection.nextLevel}</strong>
            </span>
          </div>
          <div className="relative w-full h-1.5 bg-ink-700 rounded-full overflow-hidden mb-1.5">
            <div className="absolute inset-y-0 left-0 bg-money rounded-full transition-all"
              style={{ width: `${Math.min(100, (projection.currentXp / Math.max(1, projection.nextThreshold)) * 100)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono">
            <span>XP {projection.currentXp} / {projection.nextThreshold}</span>
            <span>+{projection.dailyXp}/j ({projection.activityLabel})</span>
          </div>
          {projection.ready ? (
            <p className="mt-1.5 text-[11px] text-money font-bold flex items-center gap-1" data-testid={`career-ready-${driver.id}`}>
              <Award className="w-3 h-3" /> Prêt à monter de niveau au prochain tick !
            </p>
          ) : projection.daysToNextLevel != null ? (
            <p className="mt-1.5 text-[11px] text-slate-300 leading-snug" data-testid={`career-eta-${driver.id}`}>
              Au rythme actuel, atteindra le niveau {projection.nextLevel} dans{' '}
              <strong className="text-money">{projection.daysToNextLevel} jour{projection.daysToNextLevel > 1 ? 's' : ''}</strong>.
              {projection.activityLabel === 'à l\'arrêt' && (
                <span className="text-slate-500"> Affecter à une mission accélère la progression (×4).</span>
              )}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-slate-500 italic">Pas de progression actuellement ({projection.activityLabel}).</p>
          )}
        </div>
      )}
      <div data-testid={`career-suggestions-${driver.id}`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Spécialités rentables aujourd&apos;hui</p>
        {suggestions.length === 0 ? (
          <p className="text-[11px] text-slate-500 italic" data-testid={`career-no-suggestions-${driver.id}`}>
            Ce chauffeur maîtrise déjà toutes les spécialités utiles aux contrats actifs.
          </p>
        ) : (
          <div className="space-y-1">
            {suggestions.slice(0, 4).map(s => {
              const isPriority = s.demandCount >= 2;
              return (
                <div key={s.trainingId}
                  className={`flex items-center justify-between rounded-md px-2 py-1.5 text-[11px] ${isPriority ? 'bg-money/10 border border-money/30' : 'bg-ink-700/30 border border-ink-600'}`}
                  data-testid={`career-suggestion-${driver.id}-${s.spec}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${isPriority ? 'text-money' : 'text-slate-300'}`}>
                      {s.name}
                      {s.demandCount > 0 && (
                        <span className="ml-1.5 font-mono text-[9px] text-slate-500">({s.demandCount} contrat·s actif·s)</span>
                      )}
                    </p>
                    {s.demandContracts.length > 0 && (
                      <p className="text-[9px] text-slate-500 truncate">{s.demandContracts.join(', ')}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 ml-2 flex-shrink-0">
                    {Math.round(s.cost / 100) / 10}k€ · {s.days}j
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-500 italic leading-relaxed border-t border-ink-600 pt-2">
        <strong className="text-slate-400">Note :</strong> Les suggestions se basent sur les contrats actuellement actifs et la priorité s&apos;adapte chaque jour.
      </p>
    </div>
  );
}
